import { useHighlightController } from "./useHighlightController";
import { appstr } from "@coreapp/utils/appstr";
import {
  Flex,
  Button,
  EditableText,
  Checkbox,
  Text,
  BigInfoIcon,
  Pill,
  Input,
  Menu
} from "@theorgchartdev/storybook";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import ActionListItem from "@/appComponents/Blocks/Composites/ActionListItem/ActionListItem";
import { QHighlight } from "@coreapp/CoreApp/QHighlight";
import { ShareFilterModal } from "@/appComponents/Blocks/Modals/ShareFilterModal/ShareFilterModal";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import {
  useSharedFilterController,
  type SharedFilterDoc
} from "@/appComponents/Navigation/RightSidebar/FilterPanel/useSharedFilterController";
import { QSharedFilterRFC } from "@coreapp/CoreApp/QSharedFilterRFC";
import { GetDoc, GetWSMgr } from "@/Utils/StateLib";
import { useTopic } from "@/hooks/useTopic";
import { confirmDeleteShared } from "@/appComponents/Navigation/RightSidebar/Utils/confirmDeleteShared";
import { TOAST_STATUS, useToast } from "@/providers/ToastProvider";
import { createBoldToastMessage } from "@/appComponents/Blocks/Cards/ToastContainer/utils";
import {
  chartModeTestId,
  planningTestId
} from "@/appComponents/Navigation/RightSidebar/Utils/chartModeTestId";

type MyHighlightsProps = {
  highlightsCtrl: ReturnType<typeof useHighlightController>;
  onEditSharedHighlight?: (highlight: SharedFilterDoc) => void;
  onOwnerActiveCountChange?: (count: number) => void;
  onViewDetails?: (data: any) => void;
};

type ColorSquareProps = {
  color?: string;
};

export const ColorSquare = ({ color }: ColorSquareProps) => {
  return (
    <div
      style={{
        backgroundColor: color,
        width: "12px",
        minWidth: "12px",
        height: "12px",
        borderRadius: "2px",
        flexShrink: 0
      }}
    />
  );
};

export const MyHighlights = (props: MyHighlightsProps) => {
  const { highlightsCtrl, onEditSharedHighlight, onOwnerActiveCountChange, onViewDetails } = props;
  const { canShareFilters: canShare } = usePermissionCheck();
  const { addToast } = useToast() as any;

  const [shareModalState, setShareModalState] = useState<{
    show: boolean;
    highlight: QHighlight | null;
    sharedDoc: SharedFilterDoc | null;
  }>({ show: false, highlight: null, sharedDoc: null });

  const {
    sharedHighlights: mySharedList,
    isBeingShared = () => false,
    fetchSharedItems,
    deleteShared
  } = useSharedFilterController();
  const [ownerEnabledIds, setOwnerEnabledIds] = useState<Set<string>>(new Set());
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortAsc, setSortAsc] = useState(true);

  useEffect(() => {
    if (canShare) {
      fetchSharedItems("highlight");
    }
  }, [canShare]);

  useTopic("chartDocumentClosed", () => {
    setOwnerEnabledIds(new Set());
  });

  // Owner's shared highlights — displayed in "My highlights" tab
  const ownerSharedDocs = useMemo(
    () => mySharedList.filter(h => h.userRole === "owner"),
    [mySharedList]
  );

  // Search and sort
  const { sortedHighlights, sortedOwnerDocs } = useMemo(() => {
    const term = searchTerm.toLowerCase();
    let highlights = [...highlightsCtrl.sortedFilteredHighlights];
    let ownerDocs = [...ownerSharedDocs];

    if (searchTerm) {
      highlights = highlights.filter(h => (h.fName || "").toLowerCase().includes(term));
      ownerDocs = ownerDocs.filter(d => (d.definition?.name || "").toLowerCase().includes(term));
    }

    highlights.sort((a, b) => {
      const nameA = (a.fName || "").toLowerCase();
      const nameB = (b.fName || "").toLowerCase();
      return sortAsc ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
    });

    ownerDocs.sort((a, b) => {
      const nameA = (a.definition?.name || "").toLowerCase();
      const nameB = (b.definition?.name || "").toLowerCase();
      return sortAsc ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
    });

    return { sortedHighlights: highlights, sortedOwnerDocs: ownerDocs };
  }, [highlightsCtrl.sortedFilteredHighlights, ownerSharedDocs, searchTerm, sortAsc]);

  // Restore toggle state from highlightMgr on mount/remount
  useEffect(() => {
    if (ownerSharedDocs.length === 0) return;
    const doc = GetDoc();
    const highlightMgr = doc?.GetHighlightMgr();
    const activeShared = highlightMgr?.GetSharedHighlights?.() || [];
    const activeDocIds = new Set(activeShared.map((h: any) => h._sharedDocId));

    const ids = new Set<string>();
    ownerSharedDocs.forEach(d => {
      if (activeDocIds.has(d._id)) ids.add(d._id);
    });
    if (ids.size > 0) {
      setOwnerEnabledIds(ids);
      onOwnerActiveCountChange?.(ids.size);
    }
  }, [ownerSharedDocs]);

  // Toggle handler for owner's shared highlights
  const handleOwnerToggle = useCallback(
    (sharedDoc: SharedFilterDoc) => {
      const doc = GetDoc();
      const wsMgr = GetWSMgr();
      const highlightMgr = doc?.GetHighlightMgr();
      if (!highlightMgr || !wsMgr) return;

      const next = new Set(ownerEnabledIds);
      if (next.has(sharedDoc._id)) next.delete(sharedDoc._id);
      else next.add(sharedDoc._id);
      setOwnerEnabledIds(next);
      onOwnerActiveCountChange?.(next.size);

      // Rebuild shared highlights: preserve non-owner, rebuild owner
      const existingShared = highlightMgr.GetSharedHighlights?.() || [];
      const ownerDocIds = new Set(ownerSharedDocs.map(d => d._id));
      const nonOwnerHighlights = existingShared.filter(
        (h: any) => !ownerDocIds.has(h._sharedDocId)
      );

      const ownerHighlights = ownerSharedDocs
        .filter(d => next.has(d._id))
        .map(d => {
          const hl = new QHighlight();
          hl.LoadFromJSON(d.definition);
          hl.SetHighlightApplied(true);
          (hl as any)._sharedDocId = d._id;
          return hl;
        });

      highlightMgr.SetSharedHighlights([...nonOwnerHighlights, ...ownerHighlights]);

      wsMgr.ApplyChartHighlights();
      wsMgr.SignalDocChanged();
    },
    [ownerEnabledIds, ownerSharedDocs]
  );

  const handleDeleteSharedHighlight = useCallback(
    (docId: string) => {
      const theDoc = GetDoc();
      const highlightMgr = theDoc?.GetHighlightMgr();
      if (highlightMgr) {
        const shared = highlightMgr.GetSharedHighlights?.() || [];
        const remaining = shared.filter((h: any) => h._sharedDocId !== docId);
        highlightMgr.SetSharedHighlights(remaining);
        const wsMgr = GetWSMgr();
        if (wsMgr) {
          wsMgr.ApplyChartHighlights();
          wsMgr.SignalDocChanged();
        }
      }
      setOwnerEnabledIds(prev => {
        const next = new Set(prev);
        next.delete(docId);
        onOwnerActiveCountChange?.(next.size);
        return next;
      });
      return deleteShared(docId).then(success => {
        if (success) fetchSharedItems("highlight");
        return success;
      });
    },
    [deleteShared, fetchSharedItems]
  );

  // After sharing a personal highlight: delete personal copy + refetch
  const handleShareComplete = () => {
    if (shareModalState.highlight) {
      highlightsCtrl.handleRemoveHighlight(shareModalState.highlight);
    }
    fetchSharedItems("highlight");
  };

  const closeShareModal = () =>
    setShareModalState({ show: false, highlight: null, sharedDoc: null });

  const wsMgr = GetWSMgr();
  const currentUserDisplayName = wsMgr?.GetUserInfo()?.GetName?.() || wsMgr?.GetUserName() || "";

  const getRulesFromHighlight = (highlight: QHighlight) => {
    const def = highlight.GetHighlightRule?.() ?? (highlight as any).fDef;
    return def?.GetRulePairList?.() ?? def?.fRulePairList ?? [];
  };

  const getRulesFromDefinition = (definition: any) => {
    const hl = new QHighlight();
    hl.LoadFromJSON(definition);
    return getRulesFromHighlight(hl);
  };

  const buildPersonalMenuOptions = (highlight: QHighlight) => [
    {
      label: appstr("Copy highlight"),
      value: "copy",
      icon: "copy" as const,
      dataTestId: chartModeTestId("highlight", "highlight-item-copy-btn"),
      clickHandler: () => {
        highlightsCtrl.handleCopyHighlight(highlight.OutputToJSON());
        addToast({
          ...createBoldToastMessage("Entity copy created", { entity: highlight.fName ?? "" }),
          status: TOAST_STATUS.SUCCESS
        });
      }
    },
    {
      label: appstr("Highlight details"),
      value: "details",
      icon: "circle-info" as const,
      dataTestId: chartModeTestId("highlight", "highlight-item-details-btn"),
      clickHandler: () =>
        onViewDetails?.({
          name: highlight.fName || "",
          ownerDisplayName: currentUserDisplayName,
          isShared: false,
          userRole: "owner",
          rules: getRulesFromHighlight(highlight),
          sourceTab: "my",
          shareProps: {
            definition: highlight.OutputToJSON?.() || {},
            filterRef: highlight,
            onShareComplete: () => {
              highlightsCtrl.handleRemoveHighlight(highlight);
              fetchSharedItems("highlight");
            }
          },
          onCopy: () => highlightsCtrl.handleCopyAndEditHighlight(highlight.OutputToJSON()),
          onEdit: () => highlightsCtrl.handleStartEditing(highlight),
          onDelete: () => highlightsCtrl.handleRemoveHighlight(highlight)
        })
    }
  ];

  const buildOwnerMenuOptions = (doc: SharedFilterDoc) => [
    {
      label: appstr("Copy highlight"),
      value: "copy",
      icon: "copy" as const,
      dataTestId: chartModeTestId("highlight", "my-shared-highlight-item-copy-btn"),
      clickHandler: () => {
        highlightsCtrl.handleCopyHighlight(doc.definition);
        addToast({
          ...createBoldToastMessage("Entity copy created", {
            entity: doc.definition?.name ?? ""
          }),
          status: TOAST_STATUS.SUCCESS
        });
      }
    },
    {
      label: appstr("Highlight details"),
      value: "details",
      icon: "circle-info" as const,
      dataTestId: chartModeTestId("highlight", "my-shared-highlight-item-details-btn"),
      clickHandler: () => {
        const sharedHighlight = new QHighlight();
        sharedHighlight.LoadFromJSON(doc.definition);
        onViewDetails?.({
          name: doc.definition?.name || "",
          ownerDisplayName: currentUserDisplayName,
          isShared: isBeingShared(doc._id),
          userRole: "owner",
          rules: getRulesFromDefinition(doc.definition),
          sourceTab: "my",
          shareProps: {
            definition: doc.definition,
            filterRef: sharedHighlight,
            sharedDoc: doc,
            onShareComplete: () => fetchSharedItems("highlight")
          },
          onCopy: () => highlightsCtrl.handleCopyAndEditHighlight(doc.definition),
          onEdit: () => onEditSharedHighlight?.(doc),
          onDelete: () => handleDeleteSharedHighlight(doc._id)
        });
      }
    }
  ];

  const noHighlights =
    highlightsCtrl.sortedFilteredHighlights.length === 0 && ownerSharedDocs.length === 0;

  return (
    <Flex direction={"column"} height={"100%"}>
      <Flex className="sidebar-header-buttons" direction="column" gap="8px" padding="16px">
        <Text size="small">{appstr("My highlights info")}</Text>
        <Button
          dataTestId={planningTestId("highlight", "add-btn") ?? "highlight--add-btn"}
          modifier={"secondary"}
          size={"small"}
          iconProps={{
            name: "plus-solid"
          }}
          onClick={highlightsCtrl.handleNewHighlight}
        >
          {appstr("Create highlight")}
        </Button>
      </Flex>
      {!noHighlights && (
        <Flex className="sidebar-search-row" gap="8px" alignItems="center" padding="0 16px 8px">
          <Input
            placeholder={appstr("Search highlights")}
            value={searchTerm}
            onInput={(e: any) => setSearchTerm(e.target.value)}
            size="small"
            dataTestId="my-highlights-search"
          />
          <Button
            iconOnly
            size="small"
            modifier="tertiary"
            iconProps={{ name: "arrow-down-a-z" }}
            tooltipProps={{ message: appstr("Sort") }}
            onClick={() => setSortAsc(prev => !prev)}
            dataTestId={chartModeTestId("highlight", "my-highlights-sort-toggle")}
          />
        </Flex>
      )}
      {noHighlights ? (
        <BigInfoIcon
          color="brand"
          name="highlighter-line"
          message={appstr("No highlights created yet")}
        />
      ) : (
        <>
          <Flex
            className={"ocn-list-scrollbar"}
            flex={"1"}
            direction={"column"}
            overflow={"auto"}
            padding={"0 16px 16px"}
          >
            {/* Personal (unshared) highlights */}
            {sortedHighlights.map((highlight: QHighlight) => {
              const personalMenuOpen = activeMenuId === `personal-${highlight.GetUID()}`;
              return (
                <Flex key={highlight.GetUID()} className="ocn-filter-block-list-item">
                  <ActionListItem
                    padding={"0px"}
                    innerFlexProps={{ alignItems: "center" }}
                    dataTestId={`filter-block-item--${highlight.GetUID()}`}
                    actions={[
                      {
                        iconName: "ellipsis-vertical" as const,
                        active: personalMenuOpen,
                        content: (
                          <Menu
                            fillWidth={false}
                            popoverProps={{
                              onOpen: () => setActiveMenuId(`personal-${highlight.GetUID()}`),
                              onClose: () => setActiveMenuId(null)
                            }}
                            trigger={
                              <Button
                                modifier="tertiary"
                                iconOnly
                                size="small"
                                iconProps={{ name: "ellipsis-vertical" }}
                                dataTestId={chartModeTestId("highlight", "highlight-item-menu-btn")}
                              />
                            }
                            options={buildPersonalMenuOptions(highlight)}
                            position="bottom-end"
                            optionSize="medium"
                            listWidth="medium"
                          />
                        )
                      },
                      ...(canShare
                        ? [
                            {
                              iconName: "share-nodes" as const,
                              active: personalMenuOpen,
                              tooltipProps: { message: appstr("Share highlight") },
                              dataTestId: chartModeTestId("highlight", "highlight-item-share-btn"),
                              onClick: () =>
                                setShareModalState({ show: true, highlight, sharedDoc: null })
                            }
                          ]
                        : []),
                      {
                        iconName: "pen-to-square" as const,
                        active: personalMenuOpen,
                        tooltipProps: { message: appstr("Edit highlight") },
                        dataTestId: chartModeTestId("highlight", "highlight-item-edit-btn"),
                        onClick: () => highlightsCtrl.handleStartEditing(highlight)
                      },
                      {
                        iconName: "trash-can" as const,
                        active: personalMenuOpen,
                        dataTestId:
                          planningTestId("highlight", "highlight-item-remove-btn") ??
                          `highlight--remove-btn`,
                        iconProps: { color: "error" },
                        tooltipProps: { message: appstr("Delete highlight") },
                        onClick: (event: any) => {
                          event.stopPropagation();
                          const name = highlight.fName ?? "";
                          highlightsCtrl.handleRemoveHighlight(highlight);
                          addToast({
                            ...createBoldToastMessage("Entity deleted", { entity: name }),
                            status: TOAST_STATUS.SUCCESS
                          });
                        }
                      }
                    ]}
                    contentBefore={
                      <React.Fragment>
                        <Checkbox
                          id={`filter-enable-group-${highlight.GetUID()}`}
                          checked={highlight.fActive ?? false}
                          size={"medium"}
                          onToggle={() => highlightsCtrl.handleHighlightToggle(highlight)}
                          data-testid={chartModeTestId("highlight", "highlight-item-checkbox")}
                        />
                        <ColorSquare color={highlight.fColor ?? ""} />
                        <EditableText
                          className="oc-fill-width"
                          bold={highlight.fActive ?? false}
                          text={highlight.fName ?? ""}
                          onChange={text => {
                            highlightsCtrl.handleHighlightNameChange(highlight, text);
                            addToast({
                              ...createBoldToastMessage("Entity rule renamed", { entity: text }),
                              status: TOAST_STATUS.SUCCESS
                            });
                          }}
                          validatorFn={text =>
                            highlightsCtrl.handleNameChangeValidation(highlight.fName ?? "", text)
                          }
                          dataTestId={chartModeTestId("highlight", "highlight-item-rename")}
                        />
                      </React.Fragment>
                    }
                  />
                </Flex>
              );
            })}

            {/* Owner's shared highlights — always show latest shared definition */}
            {sortedOwnerDocs.map(doc => {
              const ownerMenuOpen = activeMenuId === `shared-${doc._id}`;
              return (
                <Flex key={`shared-${doc._id}`} className="ocn-filter-block-list-item">
                  <ActionListItem
                    padding={"0px"}
                    innerFlexProps={{ alignItems: "center" }}
                    dataTestId={`filter-block-item--shared-${doc._id}`}
                    actions={[
                      {
                        iconName: "ellipsis-vertical" as const,
                        active: ownerMenuOpen,
                        content: (
                          <Menu
                            fillWidth={false}
                            popoverProps={{
                              onOpen: () => setActiveMenuId(`shared-${doc._id}`),
                              onClose: () => setActiveMenuId(null)
                            }}
                            trigger={
                              <Button
                                modifier="tertiary"
                                iconOnly
                                size="small"
                                iconProps={{ name: "ellipsis-vertical" }}
                                dataTestId={chartModeTestId(
                                  "highlight",
                                  "my-shared-highlight-item-menu-btn"
                                )}
                              />
                            }
                            options={buildOwnerMenuOptions(doc)}
                            position="bottom-end"
                            optionSize="medium"
                            listWidth="medium"
                          />
                        )
                      },
                      {
                        iconName: "share-nodes" as const,
                        active: ownerMenuOpen,
                        tooltipProps: { message: appstr("Share highlight") },
                        dataTestId: chartModeTestId(
                          "highlight",
                          "my-shared-highlight-item-share-btn"
                        ),
                        onClick: () =>
                          setShareModalState({ show: true, highlight: null, sharedDoc: doc })
                      },
                      {
                        iconName: "pen-to-square" as const,
                        active: ownerMenuOpen,
                        tooltipProps: { message: appstr("Edit highlight") },
                        dataTestId: chartModeTestId(
                          "highlight",
                          "my-shared-highlight-item-edit-btn"
                        ),
                        onClick: () => onEditSharedHighlight?.(doc)
                      },
                      {
                        iconName: "trash-can" as const,
                        active: ownerMenuOpen,
                        dataTestId:
                          planningTestId("highlight", "my-shared-highlight-item-remove-btn") ??
                          `shared-highlight--remove-btn`,
                        iconProps: { color: "error" },
                        tooltipProps: { message: appstr("Delete highlight") },
                        onClick: () =>
                          confirmDeleteShared(GetWSMgr(), "highlight", () => {
                            handleDeleteSharedHighlight(doc._id).then(success => {
                              if (!success) {
                                addToast({
                                  message: appstr("Shared filter delete error"),
                                  status: TOAST_STATUS.ERROR,
                                  timeoutMS: 5000
                                });
                                return;
                              }
                              addToast({
                                ...createBoldToastMessage("Entity deleted", {
                                  entity: doc.definition?.name ?? ""
                                }),
                                status: TOAST_STATUS.SUCCESS
                              });
                            });
                          })
                      }
                    ]}
                    contentBefore={
                      <React.Fragment>
                        <Checkbox
                          id={`shared-highlight-enable-${doc._id}`}
                          checked={ownerEnabledIds.has(doc._id)}
                          size={"medium"}
                          onToggle={() => handleOwnerToggle(doc)}
                          data-testid={chartModeTestId(
                            "highlight",
                            "my-shared-highlight-item-checkbox"
                          )}
                        />
                        <ColorSquare color={doc.definition?.color || ""} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <EditableText
                            bold={ownerEnabledIds.has(doc._id)}
                            text={doc.definition?.name || ""}
                            onChange={text => {
                              const wsMgr = GetWSMgr();
                              if (wsMgr) {
                                QSharedFilterRFC.UpdateSharedFilter(wsMgr, doc._id, {
                                  ...doc.definition,
                                  name: text
                                })
                                  .then((raw: any) => {
                                    const response =
                                      typeof raw === "string" ? JSON.parse(raw) : raw;
                                    if (!response?.success) {
                                      addToast({
                                        message:
                                          response?.message || appstr("Shared filter update error"),
                                        status: TOAST_STATUS.ERROR,
                                        timeoutMS: 5000
                                      });
                                      return;
                                    }
                                    fetchSharedItems("highlight");
                                    addToast({
                                      ...createBoldToastMessage("Entity rule renamed", {
                                        entity: text
                                      }),
                                      status: TOAST_STATUS.SUCCESS
                                    });
                                  })
                                  .catch((err: any) => {
                                    console.error("Failed to rename:", err);
                                    addToast({
                                      message: appstr("Shared filter update error"),
                                      status: TOAST_STATUS.ERROR,
                                      timeoutMS: 5000
                                    });
                                  });
                              }
                            }}
                            validatorFn={text =>
                              highlightsCtrl.handleNameChangeValidation(
                                doc.definition?.name || "",
                                text
                              )
                            }
                            dataTestId={chartModeTestId(
                              "highlight",
                              "my-shared-highlight-item-rename"
                            )}
                          />
                        </div>
                        <div style={{ flexShrink: 0 }}>
                          {isBeingShared(doc._id) && (
                            <Pill label={appstr("Shared")} color="info" size="compact" />
                          )}
                        </div>
                      </React.Fragment>
                    }
                  />
                </Flex>
              );
            })}
          </Flex>
        </>
      )}
      {canShare &&
        (() => {
          if (shareModalState.sharedDoc) {
            const sharedHighlight = new QHighlight();
            sharedHighlight.LoadFromJSON(shareModalState.sharedDoc.definition);
            return (
              <ShareFilterModal
                show={shareModalState.show}
                onClose={closeShareModal}
                filterType="highlight"
                definition={shareModalState.sharedDoc.definition}
                filterName={shareModalState.sharedDoc.definition?.name || ""}
                filterRef={sharedHighlight}
                onShareComplete={() => fetchSharedItems("highlight")}
                editMode={true}
                existingFilterId={shareModalState.sharedDoc._id}
                existingSharedWith={shareModalState.sharedDoc.sharedWith}
              />
            );
          }
          return (
            <ShareFilterModal
              show={shareModalState.show}
              onClose={closeShareModal}
              filterType="highlight"
              definition={shareModalState.highlight?.OutputToJSON?.() || {}}
              filterName={shareModalState.highlight?.GetName?.() || ""}
              filterRef={shareModalState.highlight}
              onShareComplete={handleShareComplete}
            />
          );
        })()}
    </Flex>
  );
};
